import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, where, getDoc, Timestamp } from 'firebase/firestore';
import { db, shipMatchWithSagawa } from '../firebase';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import AuthErrorBar from './AuthErrorBar';
import ClaimsNotification from './ClaimsNotification';
import ConfirmationDialog from './ConfirmationDialog';
import PickupDateSelector from './PickupDateSelector';
import { exportItems } from '../firebase';

// Generic Export Button Component
const ExportButton = ({ hotelId, type, label, itemCount = 0 }) => {
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');
  
  const handleExport = async () => {
    setStatus('loading');
    setMessage('Exporting...');
    try {
      const csv = await exportItems(hotelId, type);
      if (!csv) {
        setStatus('error');
        setMessage(`No ${type} items to export.`);
        setTimeout(() => setStatus('idle'), 2000);
        return;
      }
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename based on type
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = type === 'all' 
        ? `all-items-${hotelId}-${timestamp}.csv`
        : `${type}-items-${hotelId}-${timestamp}.csv`;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setStatus('success');
      setMessage('Export successful!');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (error) {
      setStatus('error');
      setMessage(`Failed to export ${type} items: ${error.message}`);
      setTimeout(() => setStatus('idle'), 2500);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button 
        onClick={handleExport} 
        style={{ margin: '1em 0', minWidth: 180 }}
        disabled={itemCount === 0 || status === 'loading'}
        title={itemCount === 0 ? `No ${type} items to export` : `Export ${itemCount} ${type} items`}
      >
        {status === 'loading' ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="spinner" style={{ width: 16, height: 16, border: '2px solid #ccc', borderTop: '2px solid #333', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
            Exporting...
          </span>
        ) : (
          `${label} (${itemCount})`
        )}
      </button>
      {status !== 'idle' && (
        <div style={{ marginTop: 6, minHeight: 24, color: status === 'error' ? '#b00020' : status === 'success' ? '#388e3c' : '#333', fontWeight: 500 }}>
          {message}
        </div>
      )}
      {/* Spinner animation keyframes */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default function AdminDashboard() {
  const { t } = useTranslation();
  
  // Styles for the dashboard header
  const dashboardHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '10px 0',
    borderBottom: '1px solid #e0e0e0'
  };

  const exportAllContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };
  const [foundItems, setFoundItems] = useState([]);
  const [lostItems, setLostItems] = useState([]);
  const [matchedItems, setMatchedItems] = useState([]);
  const [readyToShipItems, setReadyToShipItems] = useState([]);
  const [shippedItems, setShippedItems] = useState([]);
  const [discardedItems, setDiscardedItems] = useState([]);
  const [manualReturnItems, setManualReturnItems] = useState([]);
  const [filteredFoundItems, setFilteredFoundItems] = useState([]);
  const [filteredLostItems, setFilteredLostItems] = useState([]);
  const [filteredMatchedItems, setFilteredMatchedItems] = useState([]);
  const [filteredReadyToShipItems, setFilteredReadyToShipItems] = useState([]);
  const [filteredShippedItems, setFilteredShippedItems] = useState([]);
  const [filteredDiscardedItems, setFilteredDiscardedItems] = useState([]);
  const [filteredManualReturnItems, setFilteredManualReturnItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [showClaimsNotification, setShowClaimsNotification] = useState(false);
  const [previousClaims, setPreviousClaims] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('found');
  const [userClaims, setUserClaims] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showPickupDateDialog, setShowPickupDateDialog] = useState(false);
  const [selectedMatchForPickup, setSelectedMatchForPickup] = useState(null);
  const [shippingRequestLoading, setShippingRequestLoading] = useState(false);
  const [selectedFoundItemIds, setSelectedFoundItemIds] = useState(new Set());
  const [foundBatchActionLoading, setFoundBatchActionLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hotelId } = useParams();
  const { currentUser } = useAuth();

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Check user permissions and redirect staff-only users
  useEffect(() => {
    async function checkUserPermissions() {
      if (currentUser) {
        const idTokenResult = await currentUser.getIdTokenResult();
        const isStaffOnly = idTokenResult.claims.staff === true && idTokenResult.claims.admin !== true;
        setUserClaims(idTokenResult.claims);
        
        if (isStaffOnly) {
          // Redirect staff-only users to found items registration
          navigate(`/${hotelId}/found-items/register`);
          return;
        }
      }
    }
    checkUserPermissions();
  }, [currentUser, navigate, hotelId]);

  // Listen for user document changes
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = onSnapshot(
      doc(db, "users", currentUser.uid),
      (snapshot) => {
        const userData = snapshot.data();

        // Check if claims changed
        if (previousClaims &&
          (previousClaims.admin !== userData.claims?.admin ||
            previousClaims.staff !== userData.claims?.staff)) {
          setShowClaimsNotification(true);
          // Refresh page after 2 seconds to allow notification to be seen
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }

        setPreviousClaims(userData.claims);
      }
    );

    return () => unsubscribe();
  }, [currentUser, previousClaims]);

  useEffect(() => {
    if (!hotelId) return;

    setLoading(true);

    // Subscribe to found items for the specific hotel
    const foundQuery = query(
      collection(db, "found-items"),
      where("hotelId", "==", hotelId),
      where("status", "==", "pending"),
      orderBy("createdAt", "desc")
    );
    const unsubscribeFound = onSnapshot(foundQuery, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((doc) => {
        const item = { id: doc.id, ...doc.data() };
        // Only include items that aren't deleted
        if (!item.deleted) {
          items.push(item);
        }
      });
      setFoundItems(items);
      setFilteredFoundItems(items); // Initialize filtered items with all items
    });

    // Subscribe to lost items for the specific hotel
    const lostQuery = query(
      collection(db, "lost-items"),
      where("hotelId", "==", hotelId),
      where("status", "==", "pending"),
      orderBy("createdAt", "desc")
    );
    const unsubscribeLost = onSnapshot(lostQuery, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((doc) => {
        const item = { id: doc.id, ...doc.data() };
        // Only include items that aren't deleted
        if (!item.deleted) {
          items.push(item);
        }
      });
      setLostItems(items);
      setFilteredLostItems(items); // Initialize filtered items with all items
    });

    // Subscribe to matches for the specific hotel
    const matchesQuery = query(
      collection(db, "matches"),
      where("hotelId", "==", hotelId),
      where("status", "in", ["matched", "pending", "confirmed", "on-hold", "waiting-for-payment", "ready-to-ship", "scheduled-for-pickup"]),
      orderBy("matchedAt", "desc")
    );
    const unsubscribeMatches = onSnapshot(matchesQuery, async (querySnapshot) => {
      const matchesWithDetails = [];

      // Use Promise.all to fetch all related items in parallel
      const matchPromises = querySnapshot.docs.map(async (docSnap) => {
        const match = { id: docSnap.id, ...docSnap.data() };

        // Fetch the found item details
        try {
          const foundItemRef = doc(db, "found-items", match.foundItemId);
          const foundItemSnap = await getDoc(foundItemRef);
          if (foundItemSnap.exists()) {
            match.foundItem = { id: foundItemSnap.id, ...foundItemSnap.data() };
          }
        } catch (err) {
          console.error(`Error fetching found item ${match.foundItemId}:`, err);
        }

        // Fetch the lost item details
        try {
          const lostItemRef = doc(db, "lost-items", match.lostItemId);
          const lostItemSnap = await getDoc(lostItemRef);
          if (lostItemSnap.exists()) {
            match.lostItem = { id: lostItemSnap.id, ...lostItemSnap.data() };
          }
        } catch (err) {
          console.error(`Error fetching lost item ${match.lostItemId}:`, err);
        }

        return match;
      });

      // Wait for all promises to resolve
      const resolvedMatches = await Promise.all(matchPromises);

      const readyItems = resolvedMatches.filter(match => match.status === 'ready-to-ship' || match.status === 'scheduled-for-pickup');
      const activeMatches = resolvedMatches.filter(match => match.status !== 'ready-to-ship' && match.status !== 'scheduled-for-pickup');

      setReadyToShipItems(readyItems);
      setFilteredReadyToShipItems(readyItems);
      setMatchedItems(activeMatches);
      setFilteredMatchedItems(activeMatches); // Initialize filtered items with all items
    });

    // Subscribe to shipped matches for the specific hotel
    const shippedQuery = query(
      collection(db, "matches"),
      where("hotelId", "==", hotelId),
      where("status", "==", "shipped")
    );
    const unsubscribeShipped = onSnapshot(shippedQuery, async (querySnapshot) => {
      console.log("Shipped query results:", querySnapshot.docs.length, "documents");
      const shippedWithDetails = [];

      // Use Promise.all to fetch all related items in parallel
      const shippedPromises = querySnapshot.docs.map(async (docSnap) => {
        const match = { id: docSnap.id, ...docSnap.data() };
        console.log("Shipped match data:", match);

        // Fetch the found item details
        try {
          const foundItemRef = doc(db, "found-items", match.foundItemId);
          const foundItemSnap = await getDoc(foundItemRef);
          if (foundItemSnap.exists()) {
            match.foundItem = { id: foundItemSnap.id, ...foundItemSnap.data() };
          }
        } catch (err) {
          console.error(`Error fetching found item ${match.foundItemId}:`, err);
        }

        // Fetch the lost item details
        try {
          const lostItemRef = doc(db, "lost-items", match.lostItemId);
          const lostItemSnap = await getDoc(lostItemRef);
          if (lostItemSnap.exists()) {
            match.lostItem = { id: lostItemSnap.id, ...lostItemSnap.data() };
          }
        } catch (err) {
          console.error(`Error fetching lost item ${match.lostItemId}:`, err);
        }

        return match;
      });

      // Wait for all promises to resolve
      const resolvedShipped = await Promise.all(shippedPromises);
      
      // Sort by shippedAt date in JavaScript (most recent first)
      const sortedShipped = resolvedShipped.sort((a, b) => {
        if (!a.shippedAt && !b.shippedAt) return 0;
        if (!a.shippedAt) return 1;
        if (!b.shippedAt) return -1;
        return b.shippedAt.toDate() - a.shippedAt.toDate();
      });
      
      console.log("Resolved shipped items:", sortedShipped);
      setShippedItems(sortedShipped);
      setFilteredShippedItems(sortedShipped); // Initialize filtered items with all items
    }, (error) => {
      console.error("Error in shipped items query:", error);
    });

    // Subscribe to manual return matches for the specific hotel
    const manualReturnQuery = query(
      collection(db, "matches"),
      where("hotelId", "==", hotelId),
      where("status", "==", "manual-return")
    );
    const unsubscribeManualReturn = onSnapshot(manualReturnQuery, async (querySnapshot) => {
      console.log("Manual return query results:", querySnapshot.docs.length, "documents");
      const manualReturnWithDetails = [];

      // Use Promise.all to fetch all related items in parallel
      const manualReturnPromises = querySnapshot.docs.map(async (docSnap) => {
        const match = { id: docSnap.id, ...docSnap.data() };

        // Fetch found and lost item details
        const [foundDoc, lostDoc] = await Promise.all([
          match.foundItemId ? getDoc(doc(db, "found-items", match.foundItemId)) : null,
          match.lostItemId ? getDoc(doc(db, "lost-items", match.lostItemId)) : null
        ]);

        const foundItem = foundDoc?.exists() ? { id: foundDoc.id, ...foundDoc.data() } : null;
        const lostItem = lostDoc?.exists() ? { id: lostDoc.id, ...lostDoc.data() } : null;

        return {
          ...match,
          foundItem,
          lostItem
        };
      });

      // Wait for all promises to resolve
      const resolvedManualReturn = await Promise.all(manualReturnPromises);

      // Sort by manualReturnAt date in JavaScript (most recent first)
      const sortedManualReturn = resolvedManualReturn.sort((a, b) => {
        if (!a.manualReturnAt && !b.manualReturnAt) return 0;
        if (!a.manualReturnAt) return 1;
        if (!b.manualReturnAt) return -1;
        return b.manualReturnAt.toDate() - a.manualReturnAt.toDate();
      });

      console.log("Resolved manual return items:", sortedManualReturn);
      setManualReturnItems(sortedManualReturn);
      setFilteredManualReturnItems(sortedManualReturn);
    }, (error) => {
      console.error("Error in manual return items query:", error);
    });

    // Subscribe to discarded found items for the specific hotel
    const discardedFoundItemsQuery = query(
      collection(db, "found-items"),
      where("hotelId", "==", hotelId),
      where("status", "==", "disposed")
    );
    const unsubscribeDiscardedFoundItems = onSnapshot(discardedFoundItemsQuery, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((doc) => {
        const item = { id: doc.id, ...doc.data(), itemSource: 'found-item' };
        // Only include items that aren't deleted
        if (!item.deleted) {
          items.push(item);
        }
      });

      // Store found items separately to combine with disposed matches
      window.discardedFoundItems = items;
      combineDiscardedItems();
    });

    // Subscribe to disposed matches for the specific hotel
    const disposedMatchesQuery = query(
      collection(db, "matches"),
      where("hotelId", "==", hotelId),
      where("status", "==", "disposed")
    );
    const unsubscribeDisposedMatches = onSnapshot(disposedMatchesQuery, async (querySnapshot) => {
      const disposedWithDetails = [];

      // Use Promise.all to fetch all related items in parallel
      const disposedPromises = querySnapshot.docs.map(async (docSnap) => {
        const match = { id: docSnap.id, ...docSnap.data(), itemSource: 'match' };

        // Fetch found and lost item details
        const [foundDoc, lostDoc] = await Promise.all([
          match.foundItemId ? getDoc(doc(db, "found-items", match.foundItemId)) : null,
          match.lostItemId ? getDoc(doc(db, "lost-items", match.lostItemId)) : null
        ]);

        const foundItem = foundDoc?.exists() ? { id: foundDoc.id, ...foundDoc.data() } : null;
        const lostItem = lostDoc?.exists() ? { id: lostDoc.id, ...lostDoc.data() } : null;

        return {
          ...match,
          foundItem,
          lostItem
        };
      });

      // Wait for all promises to resolve
      const resolvedDisposed = await Promise.all(disposedPromises);

      // Store disposed matches separately to combine with found items
      window.disposedMatches = resolvedDisposed;
      combineDiscardedItems();
    });

    // Function to combine discarded found items and disposed matches
    function combineDiscardedItems() {
      const foundItems = window.discardedFoundItems || [];
      const matches = window.disposedMatches || [];

      const combined = [...foundItems, ...matches];

      // Sort by discardedAt/disposedAt date in JavaScript (most recent first)
      const sortedItems = combined.sort((a, b) => {
        const aDate = a.disposedAt || a.discardedAt;
        const bDate = b.disposedAt || b.discardedAt;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return bDate.toDate() - aDate.toDate();
      });

      setDiscardedItems(sortedItems);
      setFilteredDiscardedItems(sortedItems);
    }

    setLoading(false);

    return () => {
      unsubscribeFound();
      unsubscribeLost();
      unsubscribeMatches();
      unsubscribeShipped();
      unsubscribeManualReturn();
      unsubscribeDiscardedFoundItems();
      unsubscribeDisposedMatches();
    };
  }, [hotelId]);

  useEffect(() => {
    setSelectedFoundItemIds((prev) => {
      if (prev.size === 0) return prev;
      const allowedIds = new Set(filteredFoundItems.map((item) => item.id));
      const next = new Set();
      prev.forEach((id) => {
        if (allowedIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [filteredFoundItems]);

  // Apply search and date filters whenever search term or dates change
  useEffect(() => {
    // Parse search terms (split by space or comma)
    const terms = searchTerm.toLowerCase().split(/[ ,]+/).filter(term => term.length > 0);

    // Convert dates to comparable format
    const startTimestamp = startDate ? new Date(startDate).getTime() : 0;
    const endTimestamp = endDate ? new Date(`${endDate}T23:59:59`).getTime() : Infinity;

    // Filter function to check if an item matches search terms
    const matchesSearch = (item) => {
      if (terms.length === 0) return true;

      // Searchable fields for items (English)
      const englishFields = [
        item.itemType,
        item.itemName,
        item.locationLost,
        item.locationFound,
        item.hotelComplex,
        item.otherLocation,
        item.roomNumber ? `room ${item.roomNumber}` : null,
        item.description,
        item.firstName,
        item.surname,
        item.email,
        item.phone,
        item.country,
        item.storageLocation,
        item.comments,
        item.finderName
      ].filter(Boolean).map(field => field.toString().toLowerCase());

      // Add Japanese translations of searchable fields
      const japaneseFields = [];
      
      // Add localized item type if it exists
      if (item.itemType) {
        const localizedItemType = getLocalizedItemType(item.itemType);
        if (localizedItemType && localizedItemType !== item.itemType) {
          japaneseFields.push(localizedItemType.toLowerCase());
        }
      }

      // Combine both English and Japanese searchable fields
      const allSearchableFields = [...englishFields, ...japaneseFields];

      // Check if all terms match at least one field
      return terms.every(term =>
        allSearchableFields.some(field => field.includes(term))
      );
    };

    // Filter function to check if an item is within date range
    const isInDateRange = (item) => {
      if (!startDate && !endDate) return true;

      let itemDate;
      if (item.dateFound) {
        itemDate = new Date(item.dateFound);
      } else if (item.dateLost) {
        itemDate = new Date(item.dateLost);
      } else {
        return true; // If no date field, include item
      }

      const itemTimestamp = itemDate.getTime();
      return itemTimestamp >= startTimestamp && itemTimestamp <= endTimestamp;
    };

    // Filter and sort found items
    const filteredFound = foundItems.filter(item =>
      matchesSearch(item) && isInDateRange(item)
    );
    setFilteredFoundItems(sortItems(filteredFound, sortField, sortDirection));

    // Filter and sort lost items
    const filteredLost = lostItems.filter(item =>
      matchesSearch(item) && isInDateRange(item)
    );
    setFilteredLostItems(sortItems(filteredLost, sortField, sortDirection));

    // Filter and sort matched items
    const filteredMatched = matchedItems.filter(match => {
      // Combine found and lost item data for searching
      const combinedItem = {
        ...match,
        ...(match.foundItem || {}),
        ...(match.lostItem || {})
      };

      return matchesSearch(combinedItem) &&
        (isInDateRange(match.foundItem || {}) || isInDateRange(match.lostItem || {}));
    });
    setFilteredMatchedItems(sortItems(filteredMatched, sortField, sortDirection));

    // Filter and sort ready-to-ship items
    const filteredReady = readyToShipItems.filter(match => {
      const combinedItem = {
        ...match,
        ...(match.foundItem || {}),
        ...(match.lostItem || {}),
      };

      return matchesSearch(combinedItem) &&
        (isInDateRange(match.foundItem || {}) || isInDateRange(match.lostItem || {}));
    });
    setFilteredReadyToShipItems(sortItems(filteredReady, sortField, sortDirection));

    // Filter and sort shipped items
    const filteredShipped = shippedItems.filter(match => {
      // Combine found and lost item data for searching
      const combinedItem = {
        ...match,
        ...(match.foundItem || {}),
        ...(match.lostItem || {})
      };

      return matchesSearch(combinedItem) &&
        (isInDateRange(match.foundItem || {}) || isInDateRange(match.lostItem || {}));
    });
    setFilteredShippedItems(sortItems(filteredShipped, sortField, sortDirection));

    // Filter and sort manual return items
    const filteredManualReturn = manualReturnItems.filter(match => {
      // Combine found and lost item data for searching
      const combinedItem = {
        ...match,
        ...(match.foundItem || {}),
        ...(match.lostItem || {})
      };

      return matchesSearch(combinedItem) &&
        (isInDateRange(match.foundItem || {}) || isInDateRange(match.lostItem || {}));
    });
    setFilteredManualReturnItems(sortItems(filteredManualReturn, sortField, sortDirection));

    // Filter and sort discarded items
    const filteredDiscarded = discardedItems.filter(item =>
      matchesSearch(item) && isInDateRange(item)
    );
    setFilteredDiscardedItems(sortItems(filteredDiscarded, sortField, sortDirection));

  }, [searchTerm, startDate, endDate, foundItems, lostItems, matchedItems, readyToShipItems, shippedItems, manualReturnItems, discardedItems, sortField, sortDirection]);

  // Reset sort when changing tabs and set appropriate defaults
  useEffect(() => {
    switch (activeTab) {
      case 'found':
        setSortField('dateFound');
        setSortDirection('desc');
        break;
      case 'lost':
        setSortField('dateLost');
        setSortDirection('desc');
        break;
      case 'matched':
        setSortField('matchedAt');
        setSortDirection('desc');
        break;
      case 'ready':
        setSortField('matchedAt');
        setSortDirection('desc');
        break;
      case 'shipped':
        setSortField('shippedAt');
        setSortDirection('desc');
        break;
      case 'discarded':
        setSortField('discardedAt');
        setSortDirection('desc');
        break;
      default:
        setSortField('createdAt');
        setSortDirection('desc');
    }
  }, [activeTab]);

  // Check if user has required claims
  const hasRequiredClaims = previousClaims?.admin || previousClaims?.staff;

  // Helper function to get localized item type name
  const getLocalizedItemType = (itemType) => {
    if (!itemType) return t('adminDashboard.table.unknown');
    return t(`itemTypes.${itemType.toLowerCase().replace(' ', '_')}`, { defaultValue: itemType });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      if (date.toDate) { // Handle Firestore Timestamps
        return date.toDate().toLocaleDateString();
      }
      const d = new Date(date);
      if (isNaN(d.valueOf())) {
        return 'Invalid Date';
      }
      return d.toLocaleDateString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      // Handle Firestore timestamps
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString();
      }
      // Handle regular dates
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      const collectionName = activeTab === 'found' ? 'found-items' : 'lost-items';
      const itemRef = doc(db, collectionName, itemToDelete.id);
      await updateDoc(itemRef, {
        deleted: true,
        deletedAt: new Date()
      });
      setShowDeleteDialog(false);
      setItemToDelete(null);
    } catch (err) {
      console.error("Error deleting item:", err);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
  };

  const handleNavigate = (path) => {
    navigate(`/${hotelId}${path}`);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: false });
  };

  const handleSchedulePickup = async (pickupDate) => {
    if (!selectedMatchForPickup || shippingRequestLoading) {
      return;
    }

    setShippingRequestLoading(true);
    setShowPickupDateDialog(false);

    try {
      await shipMatchWithSagawa({ matchId: selectedMatchForPickup.id, pickupDate });
      // Success - the match will update via the realtime listener
    } catch (err) {
      console.error('Error scheduling pickup:', err);
      alert(`Error scheduling pickup: ${err?.message || 'Unknown error'}`);
    } finally {
      setShippingRequestLoading(false);
      setSelectedMatchForPickup(null);
    }
  };

  const handleConfirmCollected = async (matchId) => {
    if (shippingRequestLoading) return;

    setShippingRequestLoading(true);
    try {
      const matchRef = doc(db, 'matches', matchId);
      await updateDoc(matchRef, {
        status: 'shipped',
        collectedAt: Timestamp.now(),
        shippedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error confirming collection:', err);
      alert(`Error confirming collection: ${err?.message || 'Unknown error'}`);
    } finally {
      setShippingRequestLoading(false);
    }
  };

  const toggleFoundSelection = (id) => {
    setSelectedFoundItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAllFound = (checked) => {
    if (checked) {
      setSelectedFoundItemIds(new Set(filteredFoundItems.map((item) => item.id)));
      return;
    }
    setSelectedFoundItemIds(new Set());
  };

  const applyFoundBatchAction = async (action) => {
    if (selectedFoundItemIds.size === 0 || foundBatchActionLoading) return;
    const confirmMessage = action === 'dispose'
      ? t('adminDashboard.batchActions.confirmDestroy', { count: selectedFoundItemIds.size })
      : t('adminDashboard.batchActions.confirmManualReturn', { count: selectedFoundItemIds.size });
    if (!window.confirm(confirmMessage)) return;

    setFoundBatchActionLoading(true);
    const now = Timestamp.now();
    const disposerName = currentUser?.displayName || currentUser?.email || 'Unknown User';
    const disposerEmail = currentUser?.email || '';
    const disposerId = currentUser?.uid || '';

    try {
      const updates = Array.from(selectedFoundItemIds).map((id) => {
        const itemRef = doc(db, 'found-items', id);
        if (action === 'dispose') {
          return updateDoc(itemRef, {
            status: 'disposed',
            discardedAt: now,
            disposer: disposerName,
            disposerEmail,
            disposerId,
            updatedAt: now
          });
        }
        return updateDoc(itemRef, {
          status: 'manual-return',
          manualReturnAt: now,
          updatedAt: now
        });
      });

      await Promise.all(updates);
      setSelectedFoundItemIds(new Set());
    } catch (err) {
      console.error('Failed to apply batch action:', err);
      alert(`Failed to apply batch action: ${err?.message || 'Unknown error'}`);
    } finally {
      setFoundBatchActionLoading(false);
    }
  };

  // Sorting function
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Generic sorting function
  const sortItems = (items, field, direction) => {
    return [...items].sort((a, b) => {
      let aValue, bValue;

      // Handle different field types
      switch (field) {
        case 'itemType':
          aValue = a.itemType || '';
          bValue = b.itemType || '';
          break;
        case 'itemName':
          aValue = a.itemName || '';
          bValue = b.itemName || '';
          break;
        case 'dateFound':
          aValue = a.dateFound ? (a.dateFound.toDate ? new Date(a.dateFound.toDate()) : new Date(a.dateFound)) : new Date(0);
          bValue = b.dateFound ? (b.dateFound.toDate ? new Date(b.dateFound.toDate()) : new Date(b.dateFound)) : new Date(0);
          break;
        case 'dateLost':
          aValue = a.dateLost ? (a.dateLost.toDate ? new Date(a.dateLost.toDate()) : new Date(a.dateLost)) : new Date(0);
          bValue = b.dateLost ? (b.dateLost.toDate ? new Date(b.dateLost.toDate()) : new Date(b.dateLost)) : new Date(0);
          break;
        case 'location':
          aValue = a.locationLost || a.locationFound || '';
          bValue = b.locationLost || b.locationFound || '';
          break;
        case 'owner':
          aValue = `${a.firstName || ''} ${a.surname || ''}`.trim();
          bValue = `${b.firstName || ''} ${b.surname || ''}`.trim();
          break;
        case 'contact':
          aValue = a.phone || a.email || '';
          bValue = b.phone || b.email || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt.toDate()) : new Date(0);
          bValue = b.createdAt ? new Date(b.createdAt.toDate()) : new Date(0);
          break;
        // For matched/shipped items
        case 'foundItem':
          aValue = a.foundItem?.itemName || a.foundItem?.itemType || '';
          bValue = b.foundItem?.itemName || b.foundItem?.itemType || '';
          break;
        case 'lostItem':
          aValue = a.lostItem?.itemName || a.lostItem?.itemType || '';
          bValue = b.lostItem?.itemName || b.lostItem?.itemType || '';
          break;
        case 'matchedAt':
          aValue = a.matchedAt ? new Date(a.matchedAt.toDate()) : new Date(0);
          bValue = b.matchedAt ? new Date(b.matchedAt.toDate()) : new Date(0);
          break;
        case 'shippedAt':
          aValue = a.shippedAt ? new Date(a.shippedAt.toDate()) : new Date(0);
          bValue = b.shippedAt ? new Date(b.shippedAt.toDate()) : new Date(0);
          break;
        case 'discardedAt':
          aValue = a.discardedAt ? new Date(a.discardedAt.toDate()) : new Date(0);
          bValue = b.discardedAt ? new Date(b.discardedAt.toDate()) : new Date(0);
          break;
        case 'packageSize':
          aValue = a.packageSize || '';
          bValue = b.packageSize || '';
          break;
        case 'destination':
          aValue = a.destinationCountry || a.destinationCountryCode || '';
          bValue = b.destinationCountry || b.destinationCountryCode || '';
          break;
        case 'trackingCode':
          aValue = a.readyToShipCode || '';
          bValue = b.readyToShipCode || '';
          break;
        default:
          aValue = '';
          bValue = '';
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Render sortable header
  const renderSortableHeader = (label, field) => {
    const isActive = sortField === field;
    const arrow = isActive ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '';
    
    return (
      <th 
        className={`sortable-header ${isActive ? 'active' : ''}`}
        onClick={() => handleSort(field)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        {label}{arrow}
      </th>
    );
  };



  if (loading && !hotelId) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <>
      {!hasRequiredClaims && <AuthErrorBar />}
      <ClaimsNotification
        show={showClaimsNotification}
        claims={previousClaims}
        onClose={() => setShowClaimsNotification(false)}
      />
      <div className="admin-dashboard" style={{ marginTop: !hasRequiredClaims ? '60px' : '0' }}>
        <div className="dashboard-header" style={dashboardHeaderStyle}>
          <h2>{t('adminDashboard.title')}</h2>
          <div className="export-all-container" style={exportAllContainerStyle}>
              <ExportButton 
              hotelId={hotelId} 
              type="all" 
              label={t('adminDashboard.export.allItems')}
              itemCount={foundItems.length + lostItems.length + matchedItems.length + readyToShipItems.length + shippedItems.length + discardedItems.length}
            />
          </div>
        </div>

        <div className="search-filter-container">
          <div className="search-box">
            <input
              type="text"
              placeholder={t('adminDashboard.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="date-filter">
            <div className="date-input-group">
              <label>{t('adminDashboard.dateFrom')}</label>
              <div className="date-input-container">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="date-input"
                  placeholder="dd/mm/yyyy"
                />
                {startDate && (
                  <button
                    type="button"
                    className="clear-date-button"
                    onClick={() => setStartDate('')}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="date-input-group">
              <label>{t('adminDashboard.dateTo')}</label>
              <div className="date-input-container">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                  placeholder="dd/mm/yyyy"
                />
                {endDate && (
                  <button
                    type="button"
                    className="clear-date-button"
                    onClick={() => setEndDate('')}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={handleClearFilters}
              className="clear-filters-btn"
              disabled={!searchTerm && !startDate && !endDate}
            >
              {t('adminDashboard.clearFilters')}
            </button>
          </div>
        </div>

        <div className="dashboard-tabs">
          <button
            className={`tab-button ${activeTab === 'found' ? 'active' : ''}`}
            onClick={() => handleTabChange('found')}
          >
            {t('adminDashboard.tabs.foundItems')} ({filteredFoundItems.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'lost' ? 'active' : ''}`}
            onClick={() => handleTabChange('lost')}
          >
            {t('adminDashboard.tabs.lostItems')} ({filteredLostItems.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'matched' ? 'active' : ''}`}
            onClick={() => handleTabChange('matched')}
          >
            {t('adminDashboard.tabs.matchedItems')} ({filteredMatchedItems.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'ready' ? 'active' : ''}`}
            onClick={() => handleTabChange('ready')}
          >
            {t('adminDashboard.tabs.readyToShipItems')} ({filteredReadyToShipItems.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'shipped' ? 'active' : ''}`}
            onClick={() => handleTabChange('shipped')}
          >
            {t('adminDashboard.tabs.shippedItems')} ({filteredShippedItems.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'manual-return' ? 'active' : ''}`}
            onClick={() => handleTabChange('manual-return')}
          >
            {t('adminDashboard.tabs.manualReturnItems')} ({filteredManualReturnItems.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'discarded' ? 'active' : ''}`}
            onClick={() => handleTabChange('discarded')}
          >
            {t('adminDashboard.tabs.discardedItems')} ({filteredDiscardedItems.length})
          </button>
        </div>

        <div className="dashboard-content">
          <div className={`dashboard-section ${activeTab === 'found' ? 'active' : ''}`}>
            <div className="item-list-container">
              <div className="item-list-header">
                <h3>{t('adminDashboard.tabs.foundItems')}</h3>
                <button onClick={() => handleNavigate('/found-items/register')} className="add-button">
                  {t('adminDashboard.buttons.registerNewItem')}
                </button>
              </div>

              {filteredFoundItems.length === 0 ? (
                <div className="no-items">
                  <p>{t('adminDashboard.noItems.found')}</p>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 0'
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {t('adminDashboard.batchActions.selectedCount', { count: selectedFoundItemIds.size })}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        className="add-button"
                        disabled={selectedFoundItemIds.size === 0 || foundBatchActionLoading}
                        onClick={() => applyFoundBatchAction('dispose')}
                      >
                        {t('adminDashboard.batchActions.destroySelected')}
                      </button>
                      <button
                        className="add-button"
                        disabled={selectedFoundItemIds.size === 0 || foundBatchActionLoading}
                        onClick={() => applyFoundBatchAction('manual-return')}
                      >
                        {t('adminDashboard.batchActions.manualReturnSelected')}
                      </button>
                    </div>
                  </div>
                  <div className="items-table-container">
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}>
                            <input
                              type="checkbox"
                              checked={filteredFoundItems.length > 0 && selectedFoundItemIds.size === filteredFoundItems.length}
                              onChange={(e) => toggleSelectAllFound(e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={t('adminDashboard.batchActions.selectAll')}
                            />
                          </th>
                          <th>{t('adminDashboard.table.headers.image')}</th>
                          {renderSortableHeader(t('adminDashboard.table.headers.item'), 'itemType')}
                          {renderSortableHeader(t('adminDashboard.table.headers.foundDate'), 'dateFound')}
                          {renderSortableHeader(t('adminDashboard.table.headers.location'), 'location')}
                          {renderSortableHeader(t('adminDashboard.table.headers.owner'), 'owner')}
                          {renderSortableHeader(t('adminDashboard.table.headers.contact'), 'contact')}
                          {renderSortableHeader(t('adminDashboard.table.headers.status'), 'status')}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFoundItems.map(item => (
                          <tr
                            key={item.id}
                            className="item-row clickable-row"
                            onClick={() => handleNavigate(`/found-items/${item.id}`)}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedFoundItemIds.has(item.id)}
                                onChange={() => toggleFoundSelection(item.id)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={t('adminDashboard.batchActions.selectItem')}
                              />
                            </td>
                            <td className="item-thumbnail">
                              {item.imageUrls && item.imageUrls.length > 0 ? (
                                <img
                                  src={item.imageUrls[0]}
                                  alt={`${item.itemName || 'Item'} thumbnail`}
                                  className="thumbnail-image"
                                />
                              ) : (
                                <div className="no-image">{t('adminDashboard.table.noImage')}</div>
                              )}
                            </td>
                            <td className="item-cell">
                              <div className="item-type">{getLocalizedItemType(item.itemType)}</div>
                              <div className="item-name">{item.itemName || t('adminDashboard.table.unnamedItem')}</div>
                            </td>
                            <td>{formatDate(item.dateFound)}</td>
                          <td>
                            <div>
                              {[
                                item.hotelComplex,
                                item.otherLocation,
                                item.roomNumber ? `Room ${item.roomNumber}` : null
                              ].filter(Boolean).join(' / ') || t('adminDashboard.table.unknown')}
                            </div>
                          </td>
                            <td>
                              {item.firstName || item.surname ? (
                                <div>{item.firstName} {item.surname}</div>
                              ) : (
                                <div className="no-data">{t('adminDashboard.table.unknown')}</div>
                              )}
                            </td>
                            <td>
                              {item.phone ? (
                                <div className="contact-info">
                                  <div>{item.phone}</div>
                                  {item.email && <div className="email">{item.email}</div>}
                                </div>
                              ) : (
                                <div className="no-data">{t('adminDashboard.table.noContactInfo')}</div>
                              )}
                            </td>
                            <td>{item.status || t('adminDashboard.table.pending')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              
              {/* Export button for found items */}
              {foundItems.length > 0 && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <ExportButton 
                    hotelId={hotelId} 
                    type="found" 
                    label={t('adminDashboard.export.foundItems')}
                    itemCount={foundItems.length}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={`dashboard-section ${activeTab === 'lost' ? 'active' : ''}`}>
            <div className="item-list-container">
              <div className="item-list-header">
                <h3>{t('adminDashboard.tabs.lostItems')}</h3>
                <button onClick={() => handleNavigate('/report-lost-item')} className="add-button">
                  {t('adminDashboard.buttons.registerNewItem')}
                </button>
              </div>

              {filteredLostItems.length === 0 ? (
                <div className="no-items">
                  <p>{t('adminDashboard.noItems.lost')}</p>
                </div>
              ) : (
                <div className="items-table-container">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>{t('adminDashboard.table.headers.image')}</th>
                        {renderSortableHeader(t('adminDashboard.table.headers.item'), 'itemType')}
                        {renderSortableHeader(t('adminDashboard.table.headers.lostDate'), 'dateLost')}
                        {renderSortableHeader(t('adminDashboard.table.headers.location'), 'location')}
                        {renderSortableHeader(t('adminDashboard.table.headers.owner'), 'owner')}
                        {renderSortableHeader(t('adminDashboard.table.headers.contact'), 'contact')}
                        {renderSortableHeader(t('adminDashboard.table.headers.status'), 'status')}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLostItems.map(item => {
                        const rowClassName = `item-row clickable-row ${item.viewed === false ? 'unviewed' : ''}`;
                        return (
                          <tr
                            key={item.id}
                            className={rowClassName}
                            onClick={() => handleNavigate(`/lost-items/${item.id}`)}
                          >
                            <td className="item-thumbnail">
                              {item.imageUrls && item.imageUrls.length > 0 ? (
                                <img
                                  src={item.imageUrls[0]}
                                  alt={`${item.itemName || 'Item'} thumbnail`}
                                  className="thumbnail-image"
                                />
                              ) : (
                                <div className="no-image">{t('adminDashboard.table.noImage')}</div>
                              )}
                            </td>
                            <td className="item-cell">
                              <div className="item-type">{getLocalizedItemType(item.itemType)}</div>
                              <div className="item-name">{item.itemName || t('adminDashboard.table.unnamedItem')}</div>
                            </td>
                            <td>{formatDate(item.dateLost)}</td>
                            <td>
                              <div>{item.locationLost || t('adminDashboard.table.unknown')}</div>
                            </td>
                            <td>
                              {item.firstName || item.surname ? (
                                <div>{item.firstName} {item.surname}</div>
                              ) : (
                                <div className="no-data">{t('adminDashboard.table.unknown')}</div>
                              )}
                            </td>
                            <td>
                              {item.phone ? (
                                <div className="contact-info">
                                  <div>{item.phone}</div>
                                  {item.email && <div className="email">{item.email}</div>}
                                </div>
                              ) : (
                                <div className="no-data">{t('adminDashboard.table.noContactInfo')}</div>
                              )}
                            </td>
                            <td>{item.status || t('adminDashboard.table.pending')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Export button for lost items */}
              {lostItems.length > 0 && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <ExportButton 
                    hotelId={hotelId} 
                    type="lost" 
                    label={t('adminDashboard.export.lostItems')}
                    itemCount={lostItems.length}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={`dashboard-section ${activeTab === 'matched' ? 'active' : ''}`}>
            <div className="item-list-container">
              <div className="item-list-header">
                <h3>{t('adminDashboard.tabs.matchedItems')}</h3>
              </div>

              {filteredMatchedItems.length === 0 ? (
                <div className="no-items">
                  <p>{t('adminDashboard.noItems.matched')}</p>
                </div>
              ) : (
                <div className="items-table-container">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>{t('adminDashboard.table.headers.image')}</th>
                        {renderSortableHeader(t('adminDashboard.table.headers.foundItem'), 'foundItem')}
                        {renderSortableHeader(t('adminDashboard.table.headers.lostItem'), 'lostItem')}
                        {renderSortableHeader(t('adminDashboard.table.headers.matchedDate'), 'matchedAt')}
                        {renderSortableHeader(t('adminDashboard.table.headers.status'), 'status')}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMatchedItems.map(match => (
                        <tr
                          key={match.id}
                          className="item-row clickable-row"
                          onClick={() => handleNavigate(`/matches/${match.id}`)}
                        >
                          <td className="item-thumbnail">
                            {match.foundItem?.imageUrls && match.foundItem.imageUrls.length > 0 ? (
                              <img
                                src={match.foundItem.imageUrls[0]}
                                alt={`${match.foundItem.itemName || 'Item'} thumbnail`}
                                className="thumbnail-image"
                              />
                            ) : (
                              <div className="no-image">{t('adminDashboard.table.noImage')}</div>
                            )}
                          </td>
                          <td className="item-cell">
                            <div className="item-type">{getLocalizedItemType(match.foundItem?.itemType)}</div>
                            <div className="item-name">{match.foundItem?.itemName || t('adminDashboard.table.unnamedItem')}</div>
                          </td>
                          <td className="item-cell">
                            <div className="item-type">{getLocalizedItemType(match.lostItem?.itemType)}</div>
                            <div className="item-name">{match.lostItem?.itemName || t('adminDashboard.table.unnamedItem')}</div>
                          </td>
                          <td>{formatDate(match.matchedAt?.toDate())}</td>
                          <td>{match.status || t('adminDashboard.table.pending')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Export button for matched items */}
              {matchedItems.length > 0 && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <ExportButton 
                    hotelId={hotelId} 
                    type="matched" 
                    label={t('adminDashboard.export.matchedItems')}
                    itemCount={matchedItems.length}
                  />
                </div>
              )}
          </div>
        </div>

        <div className={`dashboard-section ${activeTab === 'ready' ? 'active' : ''}`}>
          <div className="item-list-container">
            <div className="item-list-header">
              <h3>{t('adminDashboard.tabs.readyToShipItems')}</h3>
            </div>

            {filteredReadyToShipItems.length === 0 ? (
              <div className="no-items">
                <p>{t('adminDashboard.noItems.ready')}</p>
              </div>
            ) : (
                <div className="items-table-container">
                <table className="items-table compact">
                  <thead>
                    <tr>
                      <th>{t('adminDashboard.table.headers.image')}</th>
                      <th>{t('adminDashboard.table.headers.item')}</th>
                      <th>{t('adminDashboard.table.headers.matchedDate')}</th>
                      <th>{t('adminDashboard.table.headers.destination')}</th>
                      <th>{t('adminDashboard.table.headers.pickupCode')}</th>
                      <th>{t('adminDashboard.table.headers.pickupDate')}</th>
                      <th>{t('adminDashboard.table.headers.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReadyToShipItems.map(match => (
                      <tr
                        key={match.id}
                        className="item-row clickable-row"
                        onClick={() => handleNavigate(`/matches/${match.id}`)}
                      >
                        <td className="item-thumbnail">
                          {match.foundItem?.imageUrls && match.foundItem.imageUrls.length > 0 ? (
                            <img
                              src={match.foundItem.imageUrls[0]}
                              alt={`${match.foundItem.itemName || 'Item'} thumbnail`}
                              className="thumbnail-image"
                            />
                          ) : (
                            <div className="no-image">{t('adminDashboard.table.noImage')}</div>
                          )}
                        </td>
                        <td className="item-cell">
                          <div className="compact-stack">
                            <div className="item-type">{getLocalizedItemType(match.foundItem?.itemType)}</div>
                            <div className="item-name">{match.foundItem?.itemName || t('adminDashboard.table.unnamedItem')}</div>
                          </div>
                        </td>
                        <td>{formatDate(match.matchedAt?.toDate())}</td>
                        <td>{match.destinationCountry || match.destinationCountryCode || t('adminDashboard.table.noData')}</td>
                        <td>
                          <div className="compact-stack">
                            <div>{match.readyToShipCode || t('adminDashboard.table.noData')}</div>
                          </div>
                        </td>
                        <td>{match.scheduledPickupDate || t('adminDashboard.table.noData')}</td>
                        <td className="action-cell">
                          {match.status === 'scheduled-for-pickup' ? (
                            <button
                              className="shipped-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConfirmCollected(match.id);
                              }}
                              disabled={shippingRequestLoading}
                            >
                              {t('matchDetails.buttons.confirmCollected')}
                            </button>
                          ) : (
                            <button
                              className="shipped-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMatchForPickup(match);
                                setShowPickupDateDialog(true);
                              }}
                              disabled={shippingRequestLoading}
                            >
                              {t('matchDetails.buttons.schedulePickup')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className={`dashboard-section ${activeTab === 'shipped' ? 'active' : ''}`}>
            <div className="item-list-container">
              <div className="item-list-header">
                <h3>{t('adminDashboard.tabs.shippedItems')}</h3>
              </div>

              {filteredShippedItems.length === 0 ? (
                <div className="no-items">
                  <p>{t('adminDashboard.noItems.shipped')}</p>
                </div>
              ) : (
                <div className="items-table-container">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>{t('adminDashboard.table.headers.image')}</th>
                        {renderSortableHeader(t('adminDashboard.table.headers.foundItem'), 'foundItem')}
                        {renderSortableHeader(t('adminDashboard.table.headers.lostItem'), 'lostItem')}
                        {renderSortableHeader(t('adminDashboard.table.headers.shippedDate'), 'shippedAt')}
                        {renderSortableHeader(t('adminDashboard.table.headers.packageSize'), 'packageSize')}
                        {renderSortableHeader(t('adminDashboard.table.headers.destination'), 'destination')}
                        <th>{t('adminDashboard.table.headers.pickupCode')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredShippedItems.map(match => (
                        <tr
                          key={match.id}
                          className="item-row clickable-row"
                          onClick={() => handleNavigate(`/matches/${match.id}`)}
                        >
                          <td className="item-thumbnail">
                            {match.foundItem?.imageUrls && match.foundItem.imageUrls.length > 0 ? (
                              <img
                                src={match.foundItem.imageUrls[0]}
                                alt={`${match.foundItem.itemName || 'Item'} thumbnail`}
                                className="thumbnail-image"
                              />
                            ) : (
                              <div className="no-image">{t('adminDashboard.table.noImage')}</div>
                            )}
                          </td>
                          <td className="item-cell">
                            <div className="item-type">{getLocalizedItemType(match.foundItem?.itemType)}</div>
                            <div className="item-name">{match.foundItem?.itemName || t('adminDashboard.table.unnamedItem')}</div>
                          </td>
                          <td className="item-cell">
                            <div className="item-type">{getLocalizedItemType(match.lostItem?.itemType)}</div>
                            <div className="item-name">{match.lostItem?.itemName || t('adminDashboard.table.unnamedItem')}</div>
                          </td>
                          <td>{formatDate(match.shippedAt?.toDate())}</td>
                          <td>{match.packageSize || t('adminDashboard.table.noData')}</td>
                          <td>{match.destinationCountry || t('adminDashboard.table.noData')}</td>
                          <td>{match.readyToShipCode || t('adminDashboard.table.noData')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Export button for shipped items */}
              {shippedItems.length > 0 && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <ExportButton 
                    hotelId={hotelId} 
                    type="shipped" 
                    label={t('adminDashboard.export.shippedItems')}
                    itemCount={shippedItems.length}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={`dashboard-section ${activeTab === 'manual-return' ? 'active' : ''}`}>
            <div className="item-list-container">
              <div className="item-list-header">
                <h3>{t('adminDashboard.tabs.manualReturnItems')}</h3>
              </div>

              {filteredManualReturnItems.length === 0 ? (
                <div className="no-items">
                  <p>{t('adminDashboard.noItems.manualReturn')}</p>
                </div>
              ) : (
                <div className="items-table-container">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>{t('adminDashboard.table.headers.image')}</th>
                        {renderSortableHeader(t('adminDashboard.table.headers.foundItem'), 'foundItem')}
                        {renderSortableHeader(t('adminDashboard.table.headers.lostItem'), 'lostItem')}
                        {renderSortableHeader(t('adminDashboard.table.headers.returnedDate'), 'manualReturnAt')}
                        <th>{t('adminDashboard.table.headers.returnType')}</th>
                        <th>{t('adminDashboard.table.headers.returnDetails')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredManualReturnItems.map(match => (
                        <tr
                          key={match.id}
                          className="item-row clickable-row"
                          onClick={() => handleNavigate(`/matches/${match.id}`)}
                        >
                          <td className="item-thumbnail">
                            {match.foundItem?.imageUrls && match.foundItem.imageUrls.length > 0 ? (
                              <img
                                src={match.foundItem.imageUrls[0]}
                                alt={`${match.foundItem.itemName || 'Item'} thumbnail`}
                                className="thumbnail-image"
                              />
                            ) : (
                              <div className="no-image">{t('adminDashboard.table.noImage')}</div>
                            )}
                          </td>
                          <td className="item-cell">
                            <div className="item-type">{getLocalizedItemType(match.foundItem?.itemType)}</div>
                            <div className="item-name">{match.foundItem?.itemName || t('adminDashboard.table.unnamedItem')}</div>
                          </td>
                          <td className="item-cell">
                            <div className="item-type">{getLocalizedItemType(match.lostItem?.itemType)}</div>
                            <div className="item-name">{match.lostItem?.itemName || t('adminDashboard.table.unnamedItem')}</div>
                          </td>
                          <td>{formatDate(match.manualReturnAt?.toDate())}</td>
                          <td>{match.returnType === 'in-person' ? t('matchDetails.returnType.inPerson') : t('matchDetails.returnType.group')}</td>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {match.returnDetails || t('adminDashboard.table.noData')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Export button for manual return items */}
              {manualReturnItems.length > 0 && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <ExportButton
                    hotelId={hotelId}
                    type="manual-return"
                    label={t('adminDashboard.export.manualReturnItems')}
                    itemCount={manualReturnItems.length}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={`dashboard-section ${activeTab === 'discarded' ? 'active' : ''}`}>
            <div className="item-list-container">
              <div className="item-list-header">
                <h3>{t('adminDashboard.tabs.discardedItems')}</h3>
              </div>

              {filteredDiscardedItems.length === 0 ? (
                <div className="no-items">
                  <p>{t('adminDashboard.noItems.discarded')}</p>
                </div>
              ) : (
                <div className="items-table-container">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>{t('adminDashboard.table.headers.image')}</th>
                        {renderSortableHeader(t('adminDashboard.table.headers.item'), 'itemType')}
                        {renderSortableHeader(t('adminDashboard.table.headers.discardedDate'), 'discardedAt')}
                        <th>{t('adminDashboard.table.headers.disposer')}</th>
                        {renderSortableHeader(t('adminDashboard.table.headers.location'), 'location')}
                        {renderSortableHeader(t('adminDashboard.table.headers.owner'), 'owner')}
                        {renderSortableHeader(t('adminDashboard.table.headers.contact'), 'contact')}
                        {renderSortableHeader(t('adminDashboard.table.headers.status'), 'status')}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDiscardedItems.map(item => {
                        // Check if this is a match or a found item
                        const isMatch = item.itemSource === 'match';
                        const displayItem = isMatch ? item.foundItem : item;
                        const imageUrl = displayItem?.imageUrls?.[0];
                        const navigatePath = isMatch ? `/matches/${item.id}` : `/found-items/${item.id}`;

                        return (
                          <tr
                            key={item.id}
                            className="item-row clickable-row"
                            onClick={() => handleNavigate(navigatePath)}
                          >
                            <td className="item-thumbnail">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={`${displayItem?.itemName || 'Item'} thumbnail`}
                                  className="thumbnail-image"
                                />
                              ) : (
                                <div className="no-image">{t('adminDashboard.table.noImage')}</div>
                              )}
                            </td>
                            <td className="item-cell">
                              <div className="item-type">{getLocalizedItemType(displayItem?.itemType)}</div>
                              <div className="item-name">{displayItem?.itemName || t('adminDashboard.table.unnamedItem')}</div>
                            </td>
                            <td>{formatDate(item.disposedAt || item.discardedAt)}</td>
                            <td>{item.disposer || displayItem?.disposer || t('adminDashboard.table.unknown')}</td>
                            <td>
                              <div>{displayItem?.locationFound || t('adminDashboard.table.unknown')}</div>
                            </td>
                            <td>
                              {displayItem?.firstName || displayItem?.surname ? (
                                <div>{displayItem.firstName} {displayItem.surname}</div>
                              ) : (
                                <div className="no-data">{t('adminDashboard.table.unknown')}</div>
                              )}
                            </td>
                            <td>
                              {displayItem?.phone ? (
                                <div className="contact-info">
                                  <div>{displayItem.phone}</div>
                                  {displayItem.email && <div className="email">{displayItem.email}</div>}
                                </div>
                              ) : (
                                <div className="no-data">{t('adminDashboard.table.noContactInfo')}</div>
                              )}
                            </td>
                            <td>{item.status || t('adminDashboard.table.pending')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Export button for discarded items */}
              {discardedItems.length > 0 && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <ExportButton 
                    hotelId={hotelId} 
                    type="discarded" 
                    label={t('adminDashboard.export.discardedItems')}
                    itemCount={discardedItems.length}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PickupDateSelector
        isOpen={showPickupDateDialog}
        onClose={() => {
          setShowPickupDateDialog(false);
          setSelectedMatchForPickup(null);
        }}
        onConfirm={handleSchedulePickup}
        loading={shippingRequestLoading}
      />
    </>
  );
}
